import React, { useId } from "react";
import styled from "styled-components";
import { bodyType, colorTransition, unselectable } from "./base/mixins";

export interface ToggleProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
	label?: React.ReactNode;
}

/**
 * §8: track 38×20, knob 16px white with a 2px inset. On is the purple fill for
 * the same reason as the checkbox — the knob is white, and white on lime is
 * 2.70:1.
 *
 * The label sits to the *left*, toggle right-aligned in the row.
 */
const Toggle = React.forwardRef<HTMLInputElement, ToggleProps>(
	({ label, className, id, ...rest }, ref) => {
		const generated = useId();
		const inputId = id ?? generated;

		return (
			<Row className={className} htmlFor={inputId}>
				{label ? <span className="hds-toggle__label">{label}</span> : null}
				<input ref={ref} id={inputId} type="checkbox" role="switch" {...rest} />
				<span className="hds-toggle__track" aria-hidden>
					<span className="hds-toggle__knob" />
				</span>
			</Row>
		);
	},
);

Toggle.displayName = "Toggle";

const Row = styled.label`
	${unselectable}
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--space-4);
	width: 100%;
	cursor: pointer;

	.hds-toggle__label {
		${bodyType}
	}

	input {
		position: absolute;
		opacity: 0;
		width: var(--size-toggle-w);
		height: var(--size-toggle-h);
		margin: 0;
		cursor: pointer;
	}

	.hds-toggle__track {
		position: relative;
		display: inline-block;
		flex: none;
		width: var(--size-toggle-w);
		height: var(--size-toggle-h);
		border-radius: var(--radius-full);
		background: var(--color-border-strong);
		${colorTransition}
	}

	/* §5 calls for a 2px inset; deriving it from the track and knob keeps the
	   three numbers consistent if any one of them ever moves. */
	.hds-toggle__knob {
		position: absolute;
		top: calc((var(--size-toggle-h) - var(--size-toggle-knob)) / 2);
		left: calc((var(--size-toggle-h) - var(--size-toggle-knob)) / 2);
		width: var(--size-toggle-knob);
		height: var(--size-toggle-knob);
		border-radius: var(--radius-full);
		background: var(--color-surface);
		/* §9 allows colour transitions only; this one moves, so keep it separate
		   and equally brief. */
		transition: transform var(--transition-base);
	}

	input:checked ~ .hds-toggle__track {
		background: var(--color-secondary-fill);
	}
	input:checked ~ .hds-toggle__track .hds-toggle__knob {
		transform: translateX(
			calc(var(--size-toggle-w) - var(--size-toggle-knob) - (var(--size-toggle-h) - var(--size-toggle-knob)))
		);
	}

	input:focus-visible ~ .hds-toggle__track {
		outline: var(--focus-ring);
		outline-offset: var(--focus-ring-offset);
	}

	input:disabled ~ * {
		opacity: 0.45;
	}
	input:disabled {
		cursor: not-allowed;
	}
`;

export default Toggle;
